import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Columns3, Receipt, FileText, LogOut, Plug, Settings, KeyRound, Users } from "lucide-react";
import CoreLogo from "@/components/CoreLogo";
import { useTheme } from "@/contexts/ThemeContext";

const BASE_NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/pipeline", label: "Deal Pipeline", icon: Columns3, testid: "nav-pipeline" },
  { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses" },
  { to: "/report", label: "Tax Report", icon: FileText, testid: "nav-report" },
  { to: "/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
  { to: "/settings/account", label: "Account Security", icon: KeyRound, testid: "nav-account-security" },
  { to: "/settings/brokerage", label: "Brokerage Theme", icon: Settings, testid: "nav-brokerage-settings" },
];

const ADMIN_NAV_ITEMS = [
  { to: "/settings/team", label: "Team", icon: Users, testid: "nav-team" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { brokerageName, theme, isAdmin } = useTheme();
  const nav = useNavigate();
  const navItems = isAdmin ? [...BASE_NAV_ITEMS, ...ADMIN_NAV_ITEMS] : BASE_NAV_ITEMS;

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      {/* Header */}
      <header className="border-b border-neutral-200 print:hidden">
        <div className="grid grid-cols-12 items-center">
          <div className="col-span-3 lg:col-span-2 px-6 py-4 border-r border-neutral-200">
            <div className="flex items-center gap-3">
              <div className="text-neutral-900" data-testid="app-shell-core-logo">
                <CoreLogo variant="mark" size={36} />
              </div>
              <div className="flex flex-col">
                <span className="font-serif text-xl tracking-tight leading-none">Ledger</span>
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-400 mt-1" data-testid="app-shell-brokerage-label">by {brokerageName}</span>
              </div>
            </div>
          </div>
          <div className="col-span-9 lg:col-span-10 px-6 py-3 flex items-center justify-between">
            <div className="hidden sm:block font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
              {user?.full_name} {user?.brokerage ? `· ${user.brokerage}` : ""}
            </div>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              className="inline-flex items-center gap-2 text-xs font-sans uppercase tracking-[0.2em] text-neutral-600 hover:text-neutral-900 transition-colors"
              data-testid="header-logout-button"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="grid grid-cols-12 min-h-[calc(100vh-57px)]">
        {/* Sidebar */}
        <aside className="col-span-12 lg:col-span-2 border-r border-neutral-200 print:hidden">
          <nav className="flex lg:flex-col overflow-x-auto">
            {navItems.map(({ to, label, icon: Icon, testid, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-4 text-sm font-sans border-b border-neutral-200 lg:border-b lg:border-l-2 transition-colors ${
                    isActive
                      ? "bg-neutral-50 text-neutral-900 lg:border-l-neutral-900"
                      : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 lg:border-l-transparent"
                  }`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:block p-6 border-t border-neutral-200 mt-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 mb-2">Tip</div>
            <p className="text-xs text-neutral-500 leading-relaxed">
              Closed deals automatically roll into your <span className="text-neutral-900">Tax Report</span>. Log expenses as you go.
            </p>
            {theme?.footer_text && (
              <p className="mt-6 pt-4 border-t border-neutral-200 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-400 leading-relaxed" data-testid="sidebar-footer-text">
                {theme.footer_text}
              </p>
            )}
          </div>
        </aside>

        {/* Content */}
        <main className="col-span-12 lg:col-span-10 bg-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
